import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const TableManagement = () => {
  const { t } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const [tables, setTables] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ number: '', capacity: '' });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      const res = await axios.get(`${API}/tables`);
      setTables(res.data);
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        // Update existing table
        await axios.put(`${API}/tables/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new table
        await axios.post(`${API}/tables`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(t('messages.success'));
      setDialogOpen(false);
      setFormData({ number: '', capacity: '' });
      setEditMode(false);
      setEditId(null);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error'));
    }
  };

  const openDialog = (table = null) => {
    if (table) {
      setEditMode(true);
      setEditId(table.id);
      setFormData({ number: table.number, capacity: table.capacity });
    } else {
      setEditMode(false);
      setEditId(null);
      setFormData({ number: '', capacity: '' });
    }
    setDialogOpen(true);
  };

  const handleDelete = (table) => {
    setTableToDelete(table);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!tableToDelete) return;
    try {
      await axios.delete(`${API}/tables/${tableToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      setDeleteDialogOpen(false);
      setTableToDelete(null);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: 'badge-success',
      occupied: 'badge-danger',
      reserved: 'badge-warning'
    };
    return badges[status] || 'badge-secondary';
  };

  return (
    <div className="table-management">
      <div className="section-header">
        <h2 className="section-title">{t('tables.tableManagement')}</h2>
        <Button onClick={() => openDialog()} data-testid="add-table-btn">
          <Plus size={16} /> {t('tables.addTable')}
        </Button>
      </div>

      <div className="tables-grid">
        {tables.map((table) => (
          <div key={table.id} className="table-card" data-testid={`table-card-${table.number}`}>
            <div className="table-header">
              <div className="table-number">{t('tables.tableNumber')} {table.number}</div>
              <div className="table-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDialog(table);
                  }}
                  className="edit-btn-small"
                  data-testid="edit-table-btn"
                  title="Edit table"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(table);
                  }}
                  className="delete-btn-small"
                  data-testid="delete-table-btn"
                  title="Delete table"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="table-info">
              <span>{t('tables.capacity')}: {table.capacity}</span>
              <span className={`badge ${getStatusBadge(table.status)}`} data-testid="table-status">
                {t(table.status)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="table-dialog" aria-describedby="table-dialog-description">
          <DialogHeader>
            <DialogTitle>{editMode ? t('actions.edit') + ' Table' : t('tables.addTable')}</DialogTitle>
            <p id="table-dialog-description" className="text-sm text-gray-500">
              {editMode 
                ? 'Update the table number and capacity.' 
                : 'Create a new table by specifying its number and capacity.'
              }
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>{t('tables.tableNumber')}</label>
              <input
                type="number"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) })}
                required
                data-testid="table-number-input"
              />
            </div>
            <div className="form-group">
              <label>{t('tables.capacity')}</label>
              <input
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                required
                data-testid="table-capacity-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="save-table-btn">{t('actions.save')}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700 mb-3">
              Are you sure you want to delete this table?
            </p>
            {tableToDelete && (
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <p className="font-medium text-gray-900">
                  Table {tableToDelete.number}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Capacity: {tableToDelete.capacity} people
                </p>
                <p className="text-sm text-gray-600">
                  Status: {tableToDelete.status}
                </p>
              </div>
            )}
            <p className="text-sm text-red-600 mt-3 font-medium">
              This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setTableToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .table-management {
          width: 100%;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
        }

        .tables-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }

        .table-card {
          background: hsl(var(--card));
          border-radius: 12px;
          padding: 20px;
          border: 2px solid hsl(var(--border));
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .table-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .table-number {
          font-size: 18px;
          font-weight: 700;
          color: hsl(var(--foreground));
          font-family: 'Manrope', sans-serif;
        }

        .table-actions {
          display: flex;
          gap: 8px;
        }

        .edit-btn-small,
        .delete-btn-small {
          background: none;
          border: none;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          position: relative;
          z-index: 10;
        }

        .edit-btn-small {
          color: #666;
        }

        .edit-btn-small:hover {
          background-color: #f0f0f0;
          color: #333;
        }

        .delete-btn-small {
          color: #dc3545;
        }

        .delete-btn-small:hover {
          background-color: #fee;
          color: #c82333;
        }
        
        .delete-btn-small:active {
          transform: scale(0.95);
        }

        .table-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 13px;
          color: #666;
        }

        .form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default TableManagement;
